import { REFERENTIEL } from './referentiel.js';
import type {
  Argent,
  Bien,
  CodeHypothese,
  CoutFamille,
  Estimation,
  EvenementHistorique,
  Instantane,
  Prerequis,
  Referentiel,
  Scenario,
  SeuilFiabilite,
} from './types.js';

const ESPACES = new RegExp('[\\u202f\\u00a0]', 'g');

/** « 48 000 € » ; les centimes n'apparaissent que s'ils existent. */
export function formaterEuros(m: Argent): string {
  const entier = m.amountCents % 100 === 0;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: m.currency,
    minimumFractionDigits: entier ? 0 : 2,
    maximumFractionDigits: entier ? 0 : 2,
  })
    .format(m.amountCents / 100)
    .replace(ESPACES, ' ');
}

export const fourchette = (basse: Argent, haute: Argent): string => `${formaterEuros(basse)} à ${formaterEuros(haute)}`;

export function niveauFiabilite(score: number, seuils: readonly SeuilFiabilite[] = REFERENTIEL.fiabilite): SeuilFiabilite | null {
  return [...seuils].sort((a, b) => a.jusqua - b.jusqua).find((s) => score < s.jusqua) ?? null;
}

/** Une hypothèse cochée (projet constructif, PLU) impose son scénario à la valeur (#23). */
export function scenarioPourHypotheses(base: Scenario, coches: ReadonlySet<CodeHypothese>, ref: Referentiel = REFERENTIEL): Scenario {
  return ref.hypotheses.find((h) => coches.has(h.code))?.scenario ?? base;
}

/** Parts des héritiers plus réserve, en centimes : bornes basse et haute de ce qui reste à partager. */
export function valeurNette(e: Estimation): { basse: number; haute: number } {
  const somme = (cle: 'low' | 'high') => e.perHeir.reduce((s, p) => s + p[cle].amountCents, 0) + e.reserve.amountCents;
  return { basse: somme('low'), haute: somme('high') };
}

/** Coût pour la famille (#49) : seulement une fois le tableau des vivants, l'arbre et l'estimation faits. */
export function coutPourFamille(e: Estimation, p: Prerequis, ref: Referentiel = REFERENTIEL): CoutFamille | null {
  if (!p.tableauVivants || !p.arbre || !p.estimation) return null;
  const { haute } = valeurNette(e);
  const tranche = ref.grilleCoutFamille.tranches.find((t) => t.jusquaCentimes === null || haute <= t.jusquaCentimes);
  if (!tranche) return null;
  return { montant: { amountCents: tranche.coutCentimes, currency: 'EUR' }, statut: ref.grilleCoutFamille.statut };
}

const TITRES = new Set<Bien['kind']>(['immeuble_bati', 'terrain']);

export function instantane(e: Estimation, biens: readonly Bien[], conjointRecherche = false): Instantane {
  const { basse, haute } = valeurNette(e);
  const avecTitre = biens.filter((b) => TITRES.has(b.kind));
  return {
    basse,
    haute,
    score: e.reliability.score,
    titreTrouve: avecTitre.length > 0 && avecTitre.every((b) => b.titleStatus === 'trouve'),
    solutionIncomplete: e.reliability.factors.some((f) => f.impact === 'negatif'),
    conjointRecherche,
  };
}

/** Événements de l'historique de la fiche (#24) entre deux calculs ; le premier calcul n'en écrit aucun. */
export function comparer(
  avant: Instantane | null,
  apres: Instantane,
  instant: string,
  ref: Referentiel = REFERENTIEL,
): EvenementHistorique[] {
  if (!avant) return [];
  const evenements: EvenementHistorique[] = [];
  const noter = (type: EvenementHistorique['type'], change: boolean) => {
    if (change) evenements.push({ type, libelle: ref.historique[type], instant });
  };
  noter('valeur_modifiee', avant.basse !== apres.basse || avant.haute !== apres.haute);
  noter('fiabilite', avant.score !== apres.score);
  noter('titre', avant.titreTrouve !== apres.titreTrouve);
  noter('solution_incomplete', avant.solutionIncomplete !== apres.solutionIncomplete);
  noter('conjoint_recherche', avant.conjointRecherche !== apres.conjointRecherche);
  return evenements;
}
